import { useState, useEffect } from 'react'
import {
  Box, Grid, Card, CardContent, Typography, Button, CircularProgress, Chip,
} from '@mui/material'
import {
  Assessment, TrendingUp, MenuBook, Sensors, Business, Fingerprint,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { analysisApi, kbApi, scannerApi, tenantApi, analyticsApi } from '../services/api'
import { RootState } from '../store'
import DriftBadge from '../components/DriftBadge'
import TrialBanner from '../components/TrialBanner'

const DashboardPage = () => {
  const [stats, setStats] = useState<any>({})
  const [recentAnalyses, setRecentAnalyses] = useState<any[]>([])
  const [driftAlerts, setDriftAlerts] = useState<any[]>([])
  const [driftActivity, setDriftActivity] = useState(0)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const isAdmin = user?.type === 'admin'

  useEffect(() => {
    const fetchAll = async () => {
      if (isAdmin) {
        // Platform admin: fetch everything
        const results = await Promise.allSettled([
          analysisApi.getAnalyses({ limit: 5 }),
          kbApi.getStats(),
          scannerApi.getAdminScanners(),
          tenantApi.getTenants(),
        ])

        const analyses = results[0].status === 'fulfilled' ? (results[0].value.data.analyses || results[0].value.data || []) : []
        const analysesTotal = results[0].status === 'fulfilled' ? (results[0].value.data.total ?? analyses.length) : 0
        const kbStats = results[1].status === 'fulfilled' ? results[1].value.data : {}
        const scanners = results[2].status === 'fulfilled' ? (results[2].value.data.scanners || results[2].value.data || []) : []
        const tenants = results[3].status === 'fulfilled' ? (results[3].value.data.tenants || results[3].value.data || []) : []

        setRecentAnalyses(analyses.slice(0, 5))
        setStats({
          analyses: analysesTotal,
          kbRules: kbStats.total_rules || kbStats.total || 0,
          scanners: scanners.filter((s: any) => s.status !== 'revoked').length,
          tenants: tenants.length,
        })
      } else {
        // Tenant user: backend filters by JWT tenant_id automatically
        const tenantId = user?.tenant_id
        const results = await Promise.allSettled([
          analysisApi.getAnalyses({ limit: 5 }),
          tenantId ? tenantApi.getTenantScanners(tenantId) : Promise.resolve({ data: [] }),
          tenantId ? analyticsApi.getDriftEvents(tenantId, { limit: 5, verdict: 'MATERIAL_DRIFT' }) : Promise.resolve({ data: { drift_events: [] } }),
          tenantId ? analyticsApi.getTenantCertificates(tenantId) : Promise.resolve({ data: { certificates: [] } }),
        ])

        const analyses = results[0].status === 'fulfilled' ? (results[0].value.data.analyses || results[0].value.data || []) : []
        const analysesTotal = results[0].status === 'fulfilled' ? (results[0].value.data.total ?? analyses.length) : 0
        const scanners = results[1].status === 'fulfilled' ? (results[1].value.data.scanners || results[1].value.data || []) : []
        const driftEvents = results[2].status === 'fulfilled' ? (results[2].value.data.drift_events || []) : []
        const certs = results[3].status === 'fulfilled' ? (results[3].value.data.certificates || []) : []
        // ONE definition of an alert, used by the tile and the list below.
        //
        // They disagreed: the tile counted certificates at MATERIAL_DRIFT or
        // CRITICAL_DRIFT, and the list showed the five most recent drift
        // events of ANY verdict. On a real estate — 153 STABLE, 67
        // REBASELINE, 1 COSMETIC — that rendered "Drift Alerts: 0" directly
        // above a list of five things called drift alerts.
        //
        // A STABLE event means nothing changed. Showing it under "alerts" is
        // a false alarm created by a label, and it trains a reader to ignore
        // the panel that is supposed to earn their attention.
        const ALERTING = ['MATERIAL_DRIFT', 'CRITICAL_DRIFT']
        const alerting = (e: any) => ALERTING.includes(e.verdict)
        const abfAlerts = certs.filter(
          (c: any) => ALERTING.includes(c.latest_drift_verdict)).length

        setRecentAnalyses(analyses.slice(0, 5))
        setDriftAlerts(driftEvents.filter(alerting).slice(0, 5))
        // Events that happened and are not alerts. Kept so the panel can say
        // "we are watching and nothing material changed" rather than
        // vanishing, which reads as a feature that is not running.
        setDriftActivity(driftEvents.length)
        setStats({
          analyses: analysesTotal,
          scanners: scanners.filter((s: any) => s.status !== 'revoked').length,
          abfAlerts,
        })
      }
      setLoading(false)
    }
    fetchAll()
  }, [isAdmin, user?.tenant_id])

  const StatCard = ({ title, value, icon, color, onClick }: any) => (
    <Card sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 4 } : {} }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 48, height: 48, borderRadius: 1, bgcolor: `${color}.main`, color: 'white', mr: 2,
          }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" component="div">
              {loading ? <CircularProgress size={24} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )

  const scoreColor = (score: number) => {
    if (score >= 8) return 'success'
    if (score >= 5) return 'warning'
    return 'error'
  }

  return (
    <Box>
      {!isAdmin && <TrialBanner />}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">Dashboard</Typography>
        <Typography variant="body2" color="text.secondary">
          ARIAS &mdash; The control plane for AI agents
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Analyses" value={stats.analyses || 0}
            icon={<Assessment />} color="primary"
            onClick={() => navigate('/analyses')}
          />
        </Grid>
        {isAdmin && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="KB Rules" value={stats.kbRules || 0}
              icon={<MenuBook />} color="info"
              onClick={() => navigate('/knowledge-base')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Scanners" value={stats.scanners || 0}
            icon={<Sensors />} color="warning"
            onClick={() => navigate('/scanners')}
          />
        </Grid>
        {!isAdmin && stats.abfAlerts != null && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Drift Alerts" value={stats.abfAlerts}
              icon={<Fingerprint />} color={stats.abfAlerts > 0 ? 'error' : 'success'}
              onClick={() => navigate('/certificates')}
            />
          </Grid>
        )}
        {isAdmin && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Tenants" value={stats.tenants || 0}
              icon={<Business />} color="success"
              onClick={() => navigate('/tenants')}
            />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Analyses</Typography>
              {recentAnalyses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No analyses yet. Run your first analysis to see results.
                </Typography>
              ) : (
                <Box>
                  {recentAnalyses.map((a: any) => (
                    <Box
                      key={a.analysis_id}
                      sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        p: 1.5, mb: 1, bgcolor: 'grey.50', borderRadius: 1, cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                      onClick={() => navigate(`/analyses/${a.analysis_id}`)}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {a.analysis_id?.substring(0, 16)}...
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(a.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${(a.risk_score_overall || 0).toFixed(1)}`}
                          color={scoreColor(a.risk_score_overall || 0) as any}
                          size="small"
                        />
                        {a.kb_matches?.length > 0 && (
                          <Chip label={`${a.kb_matches.length} KB`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Drift Alerts</Typography>
              {driftAlerts.length === 0 ? (
                /* Says so rather than disappearing. An absent panel reads as
                   a feature that is not running; this distinguishes "watching
                   and nothing material changed" from "nothing is watching". */
                <Typography variant="body2" color="text.secondary">
                  {driftActivity > 0
                    ? `No material drift. ${driftActivity} comparison`
                      + `${driftActivity === 1 ? '' : 's'} recorded — every one `
                      + 'stable, cosmetic, or re-baselined after a measurement '
                      + 'change.'
                    : 'No comparisons yet. An agent is compared from its '
                      + 'second scan onward.'}
                </Typography>
              ) : driftAlerts.map((e: any) => (
                /* Clickable. These rows named an agent and a verdict and went
                   nowhere, so the one thing a reader wants next — what
                   changed — was a dead end. */
                <Box
                  key={e.id}
                  onClick={() => e.certificate_id && navigate(`/certificates/${e.certificate_id}`)}
                  sx={{
                    p: 1, mb: 0.5, bgcolor: 'grey.50', borderRadius: 1,
                    cursor: e.certificate_id ? 'pointer' : 'default',
                    '&:hover': e.certificate_id ? { bgcolor: 'grey.100' } : {},
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {e.agent_id?.substring(0, 12)}
                    </Typography>
                    <DriftBadge verdict={e.verdict} />
                  </Box>
                  {e.summary && (
                    <Typography variant="caption" color="text.secondary">{e.summary}</Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                    {new Date(e.created_at).toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button fullWidth variant="outlined" startIcon={<Sensors />} onClick={() => navigate('/scanners')}>
                  Manage Scanners
                </Button>
                {isAdmin && (
                  <>
                    <Button fullWidth variant="outlined" startIcon={<MenuBook />} onClick={() => navigate('/knowledge-base')}>
                      Browse Knowledge Base
                    </Button>
                    <Button fullWidth variant="outlined" startIcon={<TrendingUp />} onClick={() => navigate('/analytics')}>
                      View Analytics
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default DashboardPage
